import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Restablecer contraseña | Mine Class" },
      { name: "description", content: "Define una nueva contraseña para tu cuenta Mine Class de forma segura." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ResetPassword,
});

function ResetPassword() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Supabase pone la sesión de recovery al parsear el hash automáticamente
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") setReady(true);
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password.length < 8) return setErr("Mínimo 8 caracteres.");
    if (password !== password2) return setErr("Las contraseñas no coinciden.");
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      navigate({ to: "/admin" });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-dvh flex items-center justify-center bg-[var(--color-surface-container-low)] px-4">
      <div className="w-full max-w-md bg-white border border-[var(--color-outline-variant)] p-8">
        <h1 className="text-2xl font-bold text-[var(--color-brand-purple)]">Nueva contraseña</h1>
        {!ready ? (
          <p className="text-sm text-[var(--color-on-surface-variant)] mt-4" role="status" aria-live="polite">
            Abre este enlace desde el correo que te enviamos. Si no ves nada, solicita uno nuevo.
          </p>
        ) : (
          <form
            onSubmit={submit}
            className="space-y-3 mt-6"
            aria-label="Definir nueva contraseña"
            aria-busy={busy}
            aria-describedby={err ? "rp-error" : undefined}
          >
            <label htmlFor="rp-password" className="sr-only">Nueva contraseña</label>
            <input
              id="rp-password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              aria-required="true"
              aria-invalid={!!err}
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Nueva contraseña"
              className="w-full border border-[var(--color-outline-variant)] px-4 py-3"
            />
            <label htmlFor="rp-password2" className="sr-only">Repetir contraseña</label>
            <input
              id="rp-password2"
              name="password_confirm"
              type="password"
              autoComplete="new-password"
              required
              aria-required="true"
              aria-invalid={!!err}
              minLength={8}
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              placeholder="Repite la contraseña"
              className="w-full border border-[var(--color-outline-variant)] px-4 py-3"
            />
            <div aria-live="assertive" aria-atomic="true" className="min-h-[1rem]">
              {err && <p id="rp-error" role="alert" className="text-sm text-red-600">{err}</p>}
            </div>
            <button
              type="submit"
              disabled={busy}
              aria-disabled={busy}
              className="w-full py-3 bg-[var(--color-brand-coral)] text-white font-bold uppercase tracking-wider disabled:opacity-50"
            >
              {busy ? "Guardando..." : "Actualizar contraseña"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}

